import type { JSX } from 'react';
import { useRef } from 'react';
import { ComposerButton, MicIcon, PaperclipIcon, SendIcon } from './composer-parts';
import { ACCEPT } from './use-attachments';
import type { VoiceState } from './use-voice';

/** Attach, voice (only where the browser can record) and send. */
export function ComposerActions({
  voice,
  canSend,
  onSend,
  onPick,
}: {
  voice: { supported: boolean; state: VoiceState; toggle: () => void };
  canSend: boolean;
  onSend: () => void;
  onPick: (files: File[]) => void;
}): JSX.Element {
  const picker = useRef<HTMLInputElement | null>(null);
  const listening = voice.state === 'listening';
  return (
    <div className="flex items-center gap-1.5">
      <input
        ref={picker}
        type="file"
        accept={ACCEPT}
        multiple
        hidden
        onChange={(e) => {
          onPick(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      <ComposerButton title="Attach file" onClick={() => picker.current?.click()}>
        <PaperclipIcon />
      </ComposerButton>
      {voice.supported && (
        <ComposerButton
          title={listening ? 'Stop recording' : 'Voice input'}
          onClick={voice.toggle}
          tone={listening ? 'bad' : 'plain'}
          disabled={voice.state === 'transcribing'}
        >
          {listening && (
            <span className="border-bad absolute inset-0 rounded-[9px] border [animation:ping-wide_1.2s_ease-out_infinite]" />
          )}
          <MicIcon />
        </ComposerButton>
      )}
      <ComposerButton
        title="Send"
        onClick={onSend}
        tone={canSend ? 'send' : 'off'}
        disabled={!canSend}
      >
        <SendIcon />
      </ComposerButton>
    </div>
  );
}
